'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Bell, ChevronDown, User, Share2, ArrowDownToLine, ShieldCheck, History, BarChart3, Link2, UserCog } from 'lucide-react';
import { toast } from 'sonner';

export default function DirectoryTopbar() {
  const router = useRouter();
  const { user, signOut, isAdmin } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      toast?.success('Logged out successfully');
      setDropdownOpen(false);
      router?.push('/');
    } catch {
      toast?.error('Failed to sign out. Please try again.');
    }
  };

  // Derive display info from real user session
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const displayEmail = user?.email || '';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <header className="sticky top-0 z-30 bg-card border-b border-border h-16 flex items-center px-4 md:px-8 gap-4">
      {/* Logo */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <AppLogo size={32} />
        <span className="font-extrabold text-base text-foreground tracking-tight hidden sm:block">
          InstantLoanMitra
        </span>
      </div>

      <div className="flex-1" />

      {/* Right actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => router.push('/notifications')}
          className="relative p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl hover:bg-muted transition-colors"
          >
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt={displayName}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-sm font-semibold text-foreground leading-none">{displayName}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[140px]">{displayEmail}</p>
            </div>
            <ChevronDown size={14} className={`text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-card rounded-xl border border-border shadow-card-lg py-1 z-50 animate-fade-in">
              <div className="px-4 py-2.5 border-b border-border">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground">User Account</p>
              </div>
              <button
                onClick={() => { router.push('/account'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <User size={15} className="text-muted-foreground" />
                My Account
              </button>
              <button
                onClick={() => { router.push('/profile'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <UserCog size={15} className="text-muted-foreground" />
                Profile Settings
              </button>
              <button
                onClick={() => { router.push('/referrals'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Share2 size={15} className="text-muted-foreground" />
                My Referrals
              </button>
              <button
                onClick={() => { router.push('/referral-link'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Link2 size={15} className="text-muted-foreground" />
                Referral Link
              </button>
              <button
                onClick={() => { router.push('/earnings-overview'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <BarChart3 size={15} className="text-muted-foreground" />
                Earnings Overview
              </button>
              <button
                onClick={() => { router.push('/payouts'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <ArrowDownToLine size={15} className="text-muted-foreground" />
                Payouts
              </button>
              <button
                onClick={() => { router.push('/payout-history'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <History size={15} className="text-muted-foreground" />
                Payout History
              </button>
              <button
                onClick={() => { router.push('/kyc-verification'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <ShieldCheck size={15} className="text-muted-foreground" />
                KYC Verification
              </button>
              <button
                onClick={() => { router.push('/notifications'); setDropdownOpen(false); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Bell size={15} className="text-muted-foreground" />
                Notifications
              </button>
              {isAdmin && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { router.push('/admin-panel'); setDropdownOpen(false); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-purple-600 hover:bg-purple-50 transition-colors font-semibold"
                  >
                    <ShieldCheck size={15} className="text-purple-600" />
                    Admin Panel
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <LogOut size={15} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}